import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft, IconCopy, IconSuccess, IconBank, IconWarning, IconPhone, IconExpense } from '../../icons';
import { ReceivedInvoice } from '../../types/receivedInvoiceTypes';
import EpcQrCode from './EpcQrCode';
import { getUnitByCode } from '../../utils/unitCodeMapping';
import { formatDate as formatDateCentral } from '../../utils/formatters';

interface ReceivedInvoiceDetailProps {
  invoice: ReceivedInvoice;
  onBack: () => void;
  /**
   * F4 (architecture-goal-durchstich-fixplan.md §2/§1 A1): whether the "Als
   * Ausgabe buchen" action is available (Premium + flag + owner — the caller
   * computes this the same way it gates `ReceivedInvoiceCard.onBookExpense`).
   * Optional + undefined-safe so existing call sites keep compiling.
   */
  canBookExpense?: boolean;
  /** Opens the expense-booking modal for this invoice. Only called when `canBookExpense` is true. */
  onBookExpense?: (invoice: ReceivedInvoice) => void;
  /**
   * F4-reloop: whether this invoice already has an associated expense
   * (same dedup guard as `ReceivedInvoiceCard.isExpensed`). When true and
   * `canBookExpense` is false, shows the "bereits übernommen" hint in place
   * of the booking button instead of leaving the user without any reason
   * for the missing button.
   */
  alreadyExpensed?: boolean;
}

// Human-readable labels for ZUGFeRD document type codes
const DOC_TYPE_LABELS: Record<string, string> = {
  '380': 'Rechnung',
  '381': 'Gutschrift',
  '383': 'Belastungsanzeige',
  '384': 'Berichtigte Rechnung',
  '386': 'Abschlagsrechnung',
  '389': 'Eigenrechnung',
  '875': 'Teilschlussrechnung',
  '876': 'Schlussrechnung',
  '877': 'Schlussrechnung',
};

// Human-readable labels for VAT category codes (EN16931)
const TAX_CATEGORY_LABELS: Record<string, string> = {
  S: 'Standard',
  Z: 'Nullsteuersatz',
  E: 'Steuerbefreit',
  AE: 'Umkehrung der Steuerschuld',
  K: 'Innergemeinschaftl. Lieferung',
  G: 'Export',
  O: 'Nicht steuerbar',
  L: 'IGIC',
  M: 'IPSI',
};

const ReceivedInvoiceDetail: React.FC<ReceivedInvoiceDetailProps> = ({
  invoice,
  onBack,
  canBookExpense = false,
  onBookExpense,
  alreadyExpensed = false,
}) => {
  const { t, i18n } = useTranslation('common');
  // Reuses the 'expenses' namespace (same key as `ReceivedInvoiceCard.onBookExpense`)
  // so the wording is identical wherever "Als Ausgabe buchen" appears (F4).
  const { t: tExpenses } = useTranslation('expenses');
  const { data } = invoice;
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '–';
    return formatDateCentral(dateStr, { locale: i18n.language });
  };

  const formatAmount = (amount: number, currency: string = data.currency) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const formatUnitCode = (code: string) => {
    const unit = getUnitByCode(code);
    return unit ? t(unit.nameKey) : code;
  };

  const primaryPaymentMeans = data.paymentMeans?.[0];
  const hasQrCodeData = primaryPaymentMeans?.iban && data.dueAmount > 0;
  const dueDatePassed = data.dueDate && new Date(data.dueDate) < new Date();

  const docTypeLabel = DOC_TYPE_LABELS[data.documentTypeCode] ?? null;

  return (
    <div className="space-y-6">

      {/* Test document warning */}
      {data.isTestDocument && (
        <div className="flex items-center gap-3 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-xl px-5 py-3">
          <IconWarning className="text-[var(--tone-warning-fg)] flex-shrink-0" />
          <p className="text-[var(--tone-warning-fg)] text-sm font-medium">
            {t('receivedInvoices.testDocumentWarning', 'Testdokument – diese Rechnung dient nur zu Testzwecken und hat keine rechtliche Wirkung.')}
          </p>
        </div>
      )}

      {/* Back button + title */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-muted hover:text-primary transition-colors text-sm"
        >
          <IconArrowLeft />
          {t('back', 'Zurück')}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-primary truncate">
              {data.sellerName || t('receivedInvoices.unknownSeller', 'Unbekannter Absender')}
            </h2>
            {docTypeLabel && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/20 text-accent-ink border border-indigo-500/30 whitespace-nowrap">
                {docTypeLabel}
              </span>
            )}
          </div>
          <p className="text-accent-ink font-mono text-sm">{data.documentNo}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-primary">{formatAmount(data.grandTotal)}</p>
          {data.dueDate && (
            <p className={`text-sm ${dueDatePassed ? 'text-[var(--tone-danger-fg)]' : 'text-muted'}`}>
              {t('receivedInvoices.dueDate', 'Fällig')}: {formatDate(data.dueDate)}
              {dueDatePassed && ' ⚠'}
            </p>
          )}
        </div>
      </div>

      {/* "Als Ausgabe buchen" (F4, architecture-goal-durchstich-fixplan.md §1 A1):
          brings this detail view to parity with `InboundDocumentDetail`, whose
          bottom action row has the same button. Only the caller decides whether
          it renders (Premium + flag + owner + not-already-expensed). */}
      {canBookExpense && onBookExpense && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onBookExpense(invoice)}
            // BUG-4 (designV5Sweep): token-konforme Success-Fläche statt roher
            // Grün-/Weiss-Utilities (in Hell wie Dunkel korrekt).
            className="inline-flex items-center gap-2 text-sm bg-[var(--tone-success-bg)] hover:bg-[var(--tone-success-border)] border border-[var(--tone-success-border)] text-[var(--tone-success-fg)] px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <IconExpense size={12} />
            {tExpenses('bookFromInvoice', 'Als Ausgabe übernehmen')}
          </button>
        </div>
      )}

      {/* F4-reloop: mirrors ReceivedInvoiceCard's "bereits übernommen" hint
          (same `expenses` namespace key) so the detail view gives a reason
          for the missing button instead of just omitting it. Mutually
          exclusive with the button above. */}
      {!canBookExpense && alreadyExpensed && (
        <div className="flex justify-end">
          <div className="inline-flex items-center gap-1.5 text-xs text-[var(--tone-success-fg)]">
            <IconSuccess size={12} className="flex-shrink-0" />
            <span title={tExpenses('alreadyBookedHint')}>{tExpenses('alreadyBooked')}</span>
          </div>
        </div>
      )}

      {/* Document notes / remarks */}
      {data.documentNotes && data.documentNotes.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4">
          <h3 className="text-xs font-semibold text-[var(--tone-warning-fg)] uppercase tracking-wider mb-2">
            {t('receivedInvoices.notes', 'Hinweise / Bemerkungen')}
          </h3>
          <div className="space-y-1">
            {data.documentNotes.map((note, idx) => (
              <p key={idx} className="text-secondary text-sm leading-relaxed whitespace-pre-wrap">{note}</p>
            ))}
          </div>
        </div>
      )}

      {/* Referenced invoices (Gutschrift / credit note references) */}
      {data.referencedInvoices && data.referencedInvoices.length > 0 && (
        <div className="bg-[var(--tone-info-bg)] border border-blue-500/30 rounded-xl px-5 py-4">
          <h3 className="text-xs font-semibold text-[var(--tone-info-fg)] uppercase tracking-wider mb-2">
            {t('receivedInvoices.referencedInvoices', 'Bezieht sich auf')}
          </h3>
          <div className="flex flex-wrap gap-3">
            {data.referencedInvoices.map((ref, idx) => (
              <div key={idx} className="bg-[var(--tone-info-bg)] rounded-lg px-3 py-1.5">
                <span className="text-[var(--tone-info-fg)] font-mono text-sm">{ref.invoiceNo}</span>
                {ref.invoiceDate && (
                  <span className="text-[var(--tone-info-fg)] text-xs ml-2">({formatDate(ref.invoiceDate)})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main grid: parties + meta */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* FROM */}
        <div className="bg-surface-raised border border-hairline rounded-xl p-5">
          <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3">
            {t('receivedInvoices.from', 'Von (Lieferant)')}
          </h3>
          <p className="text-primary font-semibold">{data.sellerName}</p>
          {data.sellerStreet && <p className="text-secondary text-sm mt-1">{data.sellerStreet}</p>}
          {(data.sellerZip || data.sellerCity) && (
            <p className="text-secondary text-sm">{[data.sellerZip, data.sellerCity].filter(Boolean).join(' ')}</p>
          )}
          {data.sellerCountry && data.sellerCountry !== 'DE' && (
            <p className="text-muted text-sm">{data.sellerCountry}</p>
          )}
          {data.sellerVatId && (
            <p className="text-muted text-xs mt-2">USt-IdNr.: {data.sellerVatId}</p>
          )}
          {data.sellerTaxId && (
            <p className="text-muted text-xs">St-Nr.: {data.sellerTaxId}</p>
          )}
          {data.sellerPhone && (
            <div className="flex items-center gap-1.5 mt-1">
              <IconPhone size={12} className="text-faint" />
              <p className="text-muted text-xs">{data.sellerPhone}</p>
            </div>
          )}
          {data.sellerEmail && (
            <p className="text-muted text-xs mt-0.5">{data.sellerEmail}</p>
          )}
        </div>

        {/* TO */}
        <div className="bg-surface-raised border border-hairline rounded-xl p-5">
          <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3">
            {t('receivedInvoices.to', 'An (Empfänger)')}
          </h3>
          <p className="text-primary font-semibold">{data.buyerName || '–'}</p>
          {data.buyerStreet && <p className="text-secondary text-sm mt-1">{data.buyerStreet}</p>}
          {(data.buyerZip || data.buyerCity) && (
            <p className="text-secondary text-sm">{[data.buyerZip, data.buyerCity].filter(Boolean).join(' ')}</p>
          )}
          {data.buyerCountry && data.buyerCountry !== 'DE' && (
            <p className="text-muted text-sm">{data.buyerCountry}</p>
          )}
          {data.buyerReference && (
            <p className="text-muted text-xs mt-2">
              {t('receivedInvoices.reference', 'Leitwegkennung')}: {data.buyerReference}
            </p>
          )}
          {data.buyerOrderReference && (
            <p className="text-muted text-xs mt-1">
              {t('receivedInvoices.buyerOrderRef', 'Bestellnummer')}: <span className="text-secondary font-mono">{data.buyerOrderReference}</span>
            </p>
          )}
          {data.contractReference && (
            <p className="text-muted text-xs mt-1">
              {t('receivedInvoices.contractRef', 'Vertragsnr.')}: <span className="text-secondary font-mono">{data.contractReference}</span>
            </p>
          )}
          {data.sellerOrderReference && (
            <p className="text-muted text-xs mt-1">
              {t('receivedInvoices.sellerOrderRef', 'Auftragsnr. Lieferant')}: <span className="text-secondary font-mono">{data.sellerOrderReference}</span>
            </p>
          )}
        </div>

        {/* Invoice meta */}
        <div className="bg-surface-raised border border-hairline rounded-xl p-5">
          <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3">
            {t('receivedInvoices.invoiceMeta', 'Rechnungsdetails')}
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted">{t('receivedInvoices.invoiceDate', 'Datum')}:</span>
              <span className="text-primary">{formatDate(data.documentDate)}</span>
            </div>
            {data.dueDate && (
              <div className="flex justify-between">
                <span className="text-muted">{t('receivedInvoices.dueDate', 'Fällig am')}:</span>
                <span className={dueDatePassed ? 'text-[var(--tone-danger-fg)] font-medium' : 'text-primary'}>
                  {formatDate(data.dueDate)}
                </span>
              </div>
            )}
            {data.deliveryDate && (
              <div className="flex justify-between">
                <span className="text-muted">{t('receivedInvoices.deliveryDate', 'Lieferdatum')}:</span>
                <span className="text-primary">{formatDate(data.deliveryDate)}</span>
              </div>
            )}
            {(data.billingPeriodStart || data.billingPeriodEnd) && (
              <div className="flex justify-between gap-2">
                <span className="text-muted flex-shrink-0">{t('receivedInvoices.billingPeriod', 'Leistungszeitraum')}:</span>
                <span className="text-primary text-right">
                  {formatDate(data.billingPeriodStart)} – {formatDate(data.billingPeriodEnd)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted">{t('receivedInvoices.currency', 'Währung')}:</span>
              <span className="text-primary">{data.currency}</span>
            </div>
            {data.paymentTerms && (
              <div className="mt-2 pt-2 border-t border-hairline">
                <p className="text-muted text-xs mb-1">{t('receivedInvoices.paymentTerms', 'Zahlungsbedingungen')}:</p>
                <p className="text-secondary text-xs leading-relaxed">{data.paymentTerms}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delivery address (when different from buyer address) */}
      {data.deliveryAddress && (
        <div className="bg-surface-raised border border-hairline rounded-xl p-5">
          <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3">
            {t('receivedInvoices.deliveryAddress', 'Lieferadresse')}
          </h3>
          <p className="text-primary font-semibold">{data.deliveryAddress.name}</p>
          {data.deliveryAddress.street && <p className="text-secondary text-sm mt-1">{data.deliveryAddress.street}</p>}
          {(data.deliveryAddress.zip || data.deliveryAddress.city) && (
            <p className="text-secondary text-sm">{[data.deliveryAddress.zip, data.deliveryAddress.city].filter(Boolean).join(' ')}</p>
          )}
          {data.deliveryAddress.country && data.deliveryAddress.country !== 'DE' && (
            <p className="text-muted text-sm">{data.deliveryAddress.country}</p>
          )}
        </div>
      )}

      {/* Line items */}
      <div className="bg-surface-raised border border-hairline rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-hairline">
          <h3 className="text-sm font-semibold text-primary">
            {t('receivedInvoices.positions', 'Positionen')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted border-b border-hairline">
                <th className="text-left px-5 py-3">{t('receivedInvoices.description', 'Beschreibung')}</th>
                <th className="text-right px-4 py-3">{t('receivedInvoices.quantity', 'Menge')}</th>
                <th className="text-right px-4 py-3">{t('receivedInvoices.unitPrice', 'Einzelpreis')}</th>
                <th className="text-right px-4 py-3">{t('receivedInvoices.taxRate', 'MwSt.')}</th>
                <th className="text-right px-5 py-3">{t('receivedInvoices.lineTotal', 'Gesamt')}</th>
              </tr>
            </thead>
            <tbody>
              {data.lineItems.map((item, idx) => (
                <tr key={item.id || idx} className="border-b border-hairline hover:bg-[rgb(var(--surface-inset-rgb)/0.3)]">
                  <td className="px-5 py-3">
                    <p className="text-primary font-medium">{item.productName}</p>
                    {item.articleNumber && (
                      <p className="text-faint text-xs mt-0.5">
                        {t('receivedInvoices.articleNo', 'Art.-Nr.')}: {item.articleNumber}
                      </p>
                    )}
                    {item.productDesc && (
                      <p className="text-muted text-xs mt-0.5">{item.productDesc}</p>
                    )}
                    {item.note && (
                      <p className="text-[var(--tone-warning-fg)] text-xs mt-0.5 italic">{item.note}</p>
                    )}
                  </td>
                  <td className="text-right px-4 py-3 text-secondary whitespace-nowrap">
                    {item.quantity} {formatUnitCode(item.unitCode)}
                  </td>
                  <td className="text-right px-4 py-3 text-secondary whitespace-nowrap">
                    {formatAmount(item.netPrice)}
                  </td>
                  <td className="text-right px-4 py-3 text-muted whitespace-nowrap">
                    {item.taxRate > 0 ? `${item.taxRate}%` : '–'}
                  </td>
                  <td className="text-right px-5 py-3 text-primary font-medium whitespace-nowrap">
                    {formatAmount(item.lineTotal)}
                  </td>
                </tr>
              ))}
              {data.lineItems.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-faint text-sm">
                    {t('receivedInvoices.noPositions', 'Keine Positionen gefunden')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summation + Tax breakdown + Payment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Summary + Tax */}
        <div className="space-y-4">
          {/* Tax breakdown */}
          {data.taxBreakdown.length > 0 && (
            <div className="bg-surface-raised border border-hairline rounded-xl p-5">
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3">
                {t('receivedInvoices.taxBreakdown', 'Steueraufschlüsselung')}
              </h3>
              <div className="space-y-2">
                {data.taxBreakdown.map((tax, idx) => (
                  <div key={idx} className="text-sm">
                    <div className="flex justify-between items-start">
                      <span className="text-muted">
                        {tax.taxRate > 0
                          ? `${t('receivedInvoices.vatRate', 'USt.')} ${tax.taxRate}%`
                          : (TAX_CATEGORY_LABELS[tax.categoryCode] ?? (tax.categoryCode || t('receivedInvoices.vatRate', 'USt.')))
                        }
                        {' '}({t('receivedInvoices.on', 'auf')} {formatAmount(tax.taxBasisAmount)}):
                      </span>
                      <span className="text-primary font-medium ml-4">{formatAmount(tax.taxAmount)}</span>
                    </div>
                    {/* Show category label for non-standard rates */}
                    {tax.categoryCode && tax.categoryCode !== 'S' && TAX_CATEGORY_LABELS[tax.categoryCode] && tax.taxRate > 0 && (
                      <p className="text-faint text-xs mt-0.5 pl-0.5">{TAX_CATEGORY_LABELS[tax.categoryCode]}</p>
                    )}
                    {tax.exemptionReason && (
                      <p className="text-faint text-xs mt-0.5 pl-0.5 italic">{tax.exemptionReason}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="bg-surface-raised border border-hairline rounded-xl p-5">
            <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3">
              {t('receivedInvoices.summary', 'Zusammenfassung')}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">{t('receivedInvoices.totalNet', 'Nettobetrag')}:</span>
                <span className="text-primary">{formatAmount(data.netTotal)}</span>
              </div>
              {data.allowancesCharges && data.allowancesCharges.length > 0 && data.allowancesCharges.map((ac, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-muted">
                    {ac.isCharge
                      ? t('receivedInvoices.surcharge', 'Zuschlag')
                      : t('receivedInvoices.discount', 'Nachlass')}
                    {(ac.reason || ac.reasonCode) ? ` (${ac.reason || ac.reasonCode})` : ''}
                    {ac.percent !== null ? ` ${ac.percent}%` : ''}:
                  </span>
                  <span className={ac.isCharge ? 'text-[var(--tone-danger-fg)]' : 'text-[var(--tone-success-fg)]'}>
                    {ac.isCharge ? '+' : '-'}{formatAmount(ac.amount)}
                  </span>
                </div>
              ))}
              <div className="flex justify-between">
                <span className="text-muted">{t('receivedInvoices.totalTax', 'Steuerbetrag')}:</span>
                <span className="text-primary">{formatAmount(data.taxTotal)}</span>
              </div>
              {data.prepaidAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted">{t('receivedInvoices.prepaid', 'Bereits bezahlt')}:</span>
                  <span className="text-[var(--tone-success-fg)]">-{formatAmount(data.prepaidAmount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-strong">
                <span className="text-primary font-semibold">{t('receivedInvoices.totalGross', 'Gesamtbetrag')}:</span>
                <span className="text-primary font-bold text-base">{formatAmount(data.grandTotal)}</span>
              </div>
              {data.dueAmount !== data.grandTotal && (
                <div className="flex justify-between text-accent-ink font-semibold">
                  <span>{t('receivedInvoices.dueAmount', 'Zahlbetrag')}:</span>
                  <span>{formatAmount(data.dueAmount)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment / QR code */}
        {primaryPaymentMeans && (
          <div className="bg-surface-raised border border-hairline rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <IconBank className="text-accent-ink" />
              <h3 className="text-sm font-semibold text-primary">
                {t('receivedInvoices.payment', 'Zahlungsdetails')}
              </h3>
            </div>

            <div className="space-y-3 text-sm mb-5">
              {primaryPaymentMeans.accountHolder && (
                <div>
                  <p className="text-xs text-muted mb-0.5">{t('receivedInvoices.accountHolder', 'Kontoinhaber')}</p>
                  <p className="text-primary">{primaryPaymentMeans.accountHolder}</p>
                </div>
              )}
              {primaryPaymentMeans.iban && (
                <div>
                  <p className="text-xs text-muted mb-0.5">{t('receivedInvoices.iban', 'IBAN')}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-primary font-mono text-xs tracking-wider flex-1 break-all">
                      {primaryPaymentMeans.iban}
                    </p>
                    <button
                      onClick={() => copyToClipboard(primaryPaymentMeans.iban!, 'iban')}
                      className="flex-shrink-0 text-muted hover:text-accent-ink transition-colors"
                      title={t('receivedInvoices.copyIban', 'IBAN kopieren')}
                    >
                      {copiedField === 'iban' ? (
                        <IconSuccess className="text-[var(--tone-success-fg)]" />
                      ) : (
                        <IconCopy />
                      )}
                    </button>
                  </div>
                </div>
              )}
              {primaryPaymentMeans.bic && (
                <div>
                  <p className="text-xs text-muted mb-0.5">{t('receivedInvoices.bic', 'BIC')}</p>
                  <p className="text-primary font-mono text-sm">{primaryPaymentMeans.bic}</p>
                </div>
              )}
              {data.paymentReference && (
                <div>
                  <p className="text-xs text-muted mb-0.5">{t('receivedInvoices.paymentReference', 'Verwendungszweck')}</p>
                  <p className="text-primary font-mono text-sm break-all">{data.paymentReference}</p>
                </div>
              )}
            </div>

            {data.skonto && (
              <div className="bg-[var(--tone-success-bg)] border border-green-500/30 rounded-xl px-4 py-3 mb-4">
                <p className="text-[var(--tone-success-fg)] text-sm font-semibold">
                  {data.skonto.percent}% {t('receivedInvoices.skonto', 'Skonto')}
                  {data.skonto.days !== null && ` ${t('receivedInvoices.skontoWithin', 'bei Zahlung binnen')} ${data.skonto.days} ${t('receivedInvoices.skontoDays', 'Tagen')}`}
                </p>
                <p className="text-[var(--tone-success-fg)] text-xs mt-0.5">
                  {t('receivedInvoices.skontoSaving', 'Ersparnis')}: {formatAmount(data.dueAmount * data.skonto.percent / 100)}
                </p>
              </div>
            )}

            {hasQrCodeData && (
              <div className="border-t border-hairline pt-4 flex justify-center">
                <EpcQrCode
                  iban={primaryPaymentMeans.iban!}
                  bic={primaryPaymentMeans.bic}
                  accountHolder={
                    primaryPaymentMeans.accountHolder || data.sellerName
                  }
                  amount={data.dueAmount}
                  invoiceNumber={data.documentNo}
                  currency={data.currency}
                  paymentReference={data.paymentReference || undefined}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivedInvoiceDetail;
